import {
  MailboxProviderSchema,
  type ImapConnectionSummary,
  type MailboxConnectionResponse,
  type MailboxProvider,
} from '@immo/api-contract';
import { z } from 'zod';

/**
 * The mailbox OAuth callback (IMM-86.3) lands on a bare `/settings/mailbox/callback`
 * — the redirect URI registered with Nylas carries no `/w/:slug` prefix, so the
 * callback page cannot read the workspace from the URL. We therefore stash the
 * workspace id, the workspace slug (to route back into settings afterwards), and
 * the chosen provider in `sessionStorage` just before redirecting the browser to
 * the provider, and read them back when the provider redirects here with the
 * one-time `code` + signed `state`. `sessionStorage` (not `localStorage`) so the
 * pending flow dies with the tab and never leaks across sessions.
 */

const STORAGE_KEY = 'immoos.mailbox.pending-connect';

// Where the member lands after a successful exchange. An enum, never a stored
// path, so there is no attacker-controlled destination (BL-017). Absent means
// the settings page, which is where every flow began before the calendar did.
const ReturnToSchema = z.enum(['settings-mailbox', 'calendar', 'company-mailboxes']);

const PendingConnectSchema = z
  .object({
    workspaceId: z.uuid(),
    workspaceSlug: z.string().min(1),
    provider: MailboxProviderSchema,
    // Absent reads as personal: a stash written before company mailboxes existed.
    purpose: z.enum(['personal', 'company']).optional(),
    returnTo: ReturnToSchema.optional(),
  })
  .strict();

export type PendingMailboxConnect = z.infer<typeof PendingConnectSchema>;

export function stashPendingMailboxConnect(pending: PendingMailboxConnect): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage can be unavailable (private mode / disabled). The connect click
    // then no-ops on the callback side; the member simply retries.
  }
}

export function readPendingMailboxConnect(): PendingMailboxConnect | null {
  let raw: string | null = null;

  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw === null) return null;

  try {
    const parsed = PendingConnectSchema.safeParse(JSON.parse(raw));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function clearPendingMailboxConnect(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // No-op — nothing to clear if storage is unavailable.
  }
}

/** The providers a NEW connection may choose here, in contract order (IMM-1170.1 / IMM-1173.1). IMAP has no calendar. */
export function offeredProviders(
  available: ReadonlyArray<MailboxProvider>,
  context: 'mailbox' | 'calendar',
): ReadonlyArray<MailboxProvider> {
  return MailboxProviderSchema.options.filter(
    (provider) => available.includes(provider) && !(context === 'calendar' && provider === 'IMAP'),
  );
}

/**
 * Hand the browser to the provider's hosted-OAuth page. A full-page navigation
 * (not a router push) — the destination is an external origin. Wrapped so tests
 * can spy on the redirect without stubbing the read-only `window.location`.
 */
export function redirectToProvider(authUrl: string): void {
  window.location.assign(authUrl);
}

export type ImapReconnect = { readonly email: string; readonly imap: ImapConnectionSummary };

// What an expired IMAP connection reopens with; derived at render, never stored (BL-068).
export function imapReconnectOf(connection: MailboxConnectionResponse | null): ImapReconnect | undefined {
  if (
    connection === null ||
    !connection.connected ||
    connection.status !== 'EXPIRED' ||
    connection.provider !== 'IMAP' ||
    connection.email === null ||
    connection.imap === undefined
  ) {
    return undefined;
  }

  return { email: connection.email, imap: connection.imap };
}
